import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Table, Pagination, TableActions, TableAction, TableData, TableDataTypes } from '../shared/types';
import { LoaderService } from '../loader.service';
import { AuthService } from '../auth.service';
import { IncentiveApprovalRequestsService } from './incentive-approval-requests.service';
import { ToasterService } from '../toaster.service';
import { Toaster, ToasterType } from '../shared/types/toaster.types';
import { SharedService } from '../shared/shared.service';
import { AUTHZ_FEATURES } from '../constants/permissions';
import { permissionSlugs } from '../utils/permission.utils';
import { AuthzAccessHintService } from '../shared/authz-access-hint/authz-access-hint.service';

@Component({
  selector: 'incentive-approval-requests',
  templateUrl: './incentive-approval-requests.component.html',
  providers: [IncentiveApprovalRequestsService]
})
export class IncentiveApprovalRequestsComponent implements OnInit {
  public authzAccessHint: { message: string; requestUrl: string } | null = null;
  public showFilters = true;
  public listFilters: {
    requestStatus?: string;
    cities?: string[];
    serviceTypes?: string[];
    types?: string[];
    operations?: string[];
    fromDate?: string;
    toDate?: string;
  } = {
    requestStatus: 'PENDING'
  };

  private readonly sortableColumns = [
    'active',
    'operation',
    'cities',
    'incentiveName',
    'serviceNames',
    'startDate',
    'endDate',
    'type',
    'priority',
  ];

  private sort: { sortBy?: string; sortOrder?: number } = {};

  public table: Table = new Table({
    headers: {
      active: 'Status',
      operation: 'Operation',
      cities: 'City',
      incentiveName: 'Name',
      serviceNames: 'Service',
      startDate: 'Start Date',
      endDate: 'End Date',
      type: 'Type',
      priority: 'Priority',
      action: 'Actions'
    },
    sortableColumnKeys: [
      'active',
      'operation',
      'cities',
      'incentiveName',
      'serviceNames',
      'startDate',
      'endDate',
      'type',
      'priority',
    ],
    pagination: new Pagination(0, 15, 150, [15, 20]),
    data: []
  });

  private cities = [];
  private services: { [name: string]: string[] } = {};

  private citiesFilter = {
    type: 'searchable-dropdown',
    title: 'City',
    values: [] as string[],
    default: [],
    key: 'approval-city'
  };

  private serviceFilter = {
    type: 'searchable-dropdown',
    title: 'Services',
    values: [] as string[],
    default: [],
    key: 'approval-services'
  };

  public filterComponents: any[] = [
    this.citiesFilter,
    this.serviceFilter,
    {
      type: 'searchable-dropdown',
      title: 'Incentive Type',
      values: this.sharedService.INCENTIVE_TYPES,
      default: [],
      key: 'approval-types'
    },
    {
      type: 'searchable-dropdown',
      title: 'Operation',
      values: ['Create', 'Update'],
      default: [],
      key: 'approval-operations'
    },
    {
      type: 'calendar',
      title: '',
      values: [],
      default: undefined,
      key: 'approval-dates'
    },
    {
      type: 'radio-button',
      title: 'Status',
      values: ['All', 'Pending', 'Approved', 'Rejected'],
      default: 'Pending',
      key: 'approval-request-status'
    }
  ];

  constructor(
    private approvalRequestsService: IncentiveApprovalRequestsService,
    private loaderService: LoaderService,
    private router: Router,
    private accessRights: AuthService,
    public toastService: ToasterService,
    private sharedService: SharedService,
    private authzAccessHintService: AuthzAccessHintService,
  ) { }

  async ngOnInit() {
    await this.fetchAllCities();
    await this.fetchTableData();
    await this.loadAccessHint();
  }

  private async loadAccessHint(): Promise<void> {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('incentive');
  }

  async fetchAllCities() {
    const citiesRes: any = await this.withLoader(() => this.sharedService.fetchCities().toPromise());
    this.cities = citiesRes.data.cities;
    this.citiesFilter.values = this.cities.map((city: any) => city.displayName);
  }

  async fetchServices(cityDisplayNames: string[]) {
    this.services = {};
    for (const cityName of cityDisplayNames) {
      const city = this.cities.find((c: any) => c.displayName === cityName);
      if (!city) {
        continue;
      }
      const res: any = await this.sharedService.fetchServices(city._id).toPromise();
      for (const serviceDetail of res.data) {
        const name = serviceDetail.service.name;
        this.services[name] = this.services[name] || [];
        this.services[name].push(serviceDetail._id);
      }
    }
    this.serviceFilter.values = Object.keys(this.services);
  }

  async fetchTableData() {
    await this.withLoader(async () => {
      const response: any = await this.approvalRequestsService.fetchApprovalRequests({
        pageNumber: this.table.pagination.pageNo,
        pageLimit: this.table.pagination.perPage,
        requestStatus: this.listFilters.requestStatus,
        cities: this.listFilters.cities,
        serviceTypes: this.listFilters.serviceTypes,
        types: this.listFilters.types,
        operations: this.listFilters.operations,
        fromDate: this.listFilters.fromDate,
        toDate: this.listFilters.toDate,
        sortBy: this.sort.sortBy,
        sortOrder: this.sort.sortOrder,
      }).toPromise();

      this.table.pagination.count = response.documentCount || 0;
      this.table.data = (response.data || []).map((row: any) => {
        const incentive = row.newValue || {};
        const cities = incentive.cities || [];
        const serviceNames = incentive.serviceNames || [];
        return {
          _id: row._id,
          requestStatus: row.requestStatus,
          createdBy: row.createdBy,
          resourceId: row.resourceId,
          resource: row.resource,
          key: row.key,
          version: row.version,
          newValue: incentive,
          oldValue: row.oldValue || null,
          active: this.formatRequestStatus(row.requestStatus),
          operation: this.formatRequestStatus(row.operation),
          cities: this.formatCityNames(cities),
          incentiveName: incentive.incentiveName || '',
          serviceNames: serviceNames.join(', '),
          startDate: incentive.startDate,
          endDate: incentive.endDate,
          type: incentive.incentiveType,
          priority: incentive.priority || 0,
          action: this.createViewAction(row),
        };
      });
    });
  }

  formatCityNames(cityIds: string[]) {
    return (cityIds || [])
      .map((id) => {
        const city = this.cities.find((c: any) => String(c._id) === String(id));
        return city ? city.displayName : id;
      })
      .join(', ');
  }

  formatRequestStatus(status: string) {
    if (!status) {
      return '';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  createViewAction(row: any) {
    const actionArr = [];
    if (this.accessRights.isAuthorizedFor(
      permissionSlugs(AUTHZ_FEATURES.INCENTIVE, ['view', 'edit', 'approve'], this.accessRights.getAuthzPanels()),
      AUTHZ_FEATURES.INCENTIVE
    )) {
      actionArr.push(
        new TableAction({
          onClick: () => this.openPreview(row),
          text: 'View'
        })
      );
    }
    return new TableData({
      actions: new TableActions(actionArr),
      type: TableDataTypes.ACTION
    });
  }

  openPreview(row: any) {
    const requestId = row && row._id;
    if (!requestId) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'Approval request id is missing.'
      }));
      return;
    }
    this.approvalRequestsService.storePreviewForRequest(requestId, {
      requestId,
      requestStatus: row.requestStatus,
      operation: row.operation,
      createdBy: row.createdBy,
      resourceId: row.resourceId,
      resource: row.resource,
      key: row.key,
      version: row.version,
      newValue: row.newValue || {},
      oldValue: row.oldValue || null,
    });
    this.router.navigate(['/incentive-approval-requests', requestId], { queryParams: { preview: 'approval' } });
  }

  async sortByHeader(header: string) {
    const sortOrder = header.includes('-') ? -1 : 1;
    const column = header.replace('-', '');
    if (this.sortableColumns.indexOf(column) < 0) {
      return;
    }
    this.sort = { sortBy: column, sortOrder };
    this.table.pagination.setPageNoToZero();
    await this.fetchTableData();
  }

  async filterChange(filters: any) {
    if (filters['approval-city'] && filters['approval-city'].length) {
      await this.withLoader(() => this.fetchServices(filters['approval-city']));
    }
  }

  applyFilters(filters: any) {
    this.table.pagination.setPageNoToZero();

    const statusLabel = filters['approval-request-status'] || 'All';
    const requestStatusMap: { [key: string]: string | undefined } = {
      All: undefined,
      Pending: 'PENDING',
      Approved: 'APPROVED',
      Rejected: 'REJECTED',
    };

    const approvalDates = filters['approval-dates'] || [];
    const fromDate = approvalDates[0];
    const toDate = approvalDates[1];
    let serviceTypeIds: string[] = [];
    for (const serviceName of (filters['approval-services'] || [])) {
      serviceTypeIds = serviceTypeIds.concat(this.services[serviceName] || []);
    }

    const cityNames = (filters['approval-city'] || []).map(String).filter(Boolean);

    this.listFilters = {
      requestStatus: requestStatusMap[statusLabel],
      cities: cityNames.length ? cityNames : undefined,
      serviceTypes: serviceTypeIds.length ? serviceTypeIds : undefined,
      types: (filters['approval-types'] || []).length ? filters['approval-types'] : undefined,
      operations: (filters['approval-operations'] || []).length
        ? filters['approval-operations'].map((op: string) => op.toLowerCase())
        : undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    };

    this.fetchTableData();
  }

  async withLoader<T>(cb: () => Promise<T>): Promise<T> {
    try {
      this.loaderService.openLoading();
      return await cb();
    } finally {
      this.loaderService.closeLoading();
    }
  }
}
